/**
 * Program: Italy
 * Language: English
 * Content: Index for all English translations
 */

import cookies from '../../common/en/cookies'
import privacy from '../../common/en/privacy'
import terms from './terms'
import { LangProps } from '../../types'

const en: LangProps = {
  /** -------------------------------------------------------------------------- *
   * Document Titles
   * -------------------------------------------------------------------------- */
  document_title_suffix: 'Dyson Trade In with Alchemy',
  document_title_home: 'Get started',
  document_title_faq: 'FAQs',
  document_title_quiz: 'Quiz',
  document_title_privacy: 'Privacy Policy',
  document_title_terms: 'Terms & Conditions',
  document_title_cookies: 'Cookies Policy',
  document_title_backoffice: 'Backoffice',
  document_title_signOut: 'Signing Out',
  document_title_notFound: 'Page not found',
  /** -------------------------------------------------------------------------- *
   * Links
   * -------------------------------------------------------------------------- */
  link_home: 'Home',
  link_tradein: 'Trade In',
  link_faq: 'FAQs',
  link_privacy: 'Privacy Policy',
  link_terms: 'Terms & Conditions',
  link_cookies: 'Cookie Policy',
  link_cookie_preferences: 'Cookie Preferences',
  powered_by: 'Powered by',
  /** -------------------------------------------------------------------------- *
   * Home
   * -------------------------------------------------------------------------- */
  home_hero_title: 'Trade in your old vacuum, save on your new Dyson',
  home_hero_pretitle: 'Dyson Trade In',
  home_hero_intro:
    'With Dyson Trade In we’ll give you up to €150 back when you purchase an eligible vacuum and trade in your old one.',
  home_hero_cta: 'Begin my trade in',
  home_hero2_title: 'Thank you for your interest',
  home_hero2_intro: 'Unfortunately the current trade-in program is now closed',
  home_hero2_outro:
    'If you have already completed your trade-in application, please ensure your trade-in product has been sent to us within 2 weeks. Please refer to our email for instructions.',
  home_hero2_cta: 'Visit Dyson Store',
  home_hero_sponsor: 'Powered by',
  home_steps_title: '3 easy steps to Trade In',
  home_step_1_title: 'Find out if you qualify',
  home_step_1_desc:
    'Take our quick questionnaire and tell us about the vacuum cleaner you would like to trade in, and we’ll tell you how much we’ll pay you for it.\n\nTo qualify, you must have purchased:\n\n- a Dyson Gen5detect™ Absolute or Dyson V15s Detect Submarine™ between April 22nd and June 2nd 2024\n\n- a Dyson V15 Detect™ Absolute between May 14th and June 2nd 2024',
  home_step_2_title: 'Send your old vacuum to us',
  home_step_2_desc:
    'We will provide you with a free pre-paid shipping label that you can use to send your trade in vacuum cleaner to us.\n\nSimply package your old vacuum product up, attach the label, and drop it off to your nearest DHL drop-off point.',
  home_step_3_title: 'Get paid!',
  home_step_3_desc:
    'After we receive your old vacuum cleaner at our facility, we will issue your payment.\n\nPayments will be made on between 30 and 45 days after receipt and will be paid into the account you provided.',
  home_steps_cta: 'Begin my trade in',
  home_faq_title: 'Do you have any questions?',
  home_faq_button: 'See our FAQs',
  home_footer_terms:
    '¹ At Dyson online, Dyson demo stores and selected partners. Cashback amount depends on the device sent in.',
  /** -------------------------------------------------------------------------- *
   * 404
   * -------------------------------------------------------------------------- */
  not_found_title: '404',
  not_found_desc: 'The page you are looking for could not be found.',
  not_found_button: 'Go back to the homepage',
  /** -------------------------------------------------------------------------- *
   * FAQ
   * -------------------------------------------------------------------------- */
  faq_hero_title: 'FAQs',
  faq_search_placeholder: 'Type to search',
  faq_topics_title: 'Browse topics',
  faq_search_title: 'Searching topics',
  faq_search_matches: 'result(s) found',
  faq_quick_links_title: 'Jump to',
  // -------------------------------------
  // Application Section
  // -------------------------------------
  faq_topic_1_title: 'Making the application',
  faq_topic_1_q1: 'Where do I go to to make my Dyson Trade In application?',
  faq_topic_1_a1: 'Visit [https://www.dyson.it](https://www.dyson.it) to make your Dyson trade in application.',
  faq_topic_1_q2: 'What are the qualifying products?',
  faq_topic_1_a2:
    'To qualify, you must have purchased:\n\n- a Dyson Gen5detect™ Absolute or Dyson V15s Detect Submarine™ between April 22nd and June 2nd 2024\n\n- a Dyson V15 Detect™ Absolute between May 14th and June 2nd 2024',
  faq_topic_1_q3: 'When does the trade in event run?',
  faq_topic_1_a3:
    'You must buy a qualifying product from https://www.dyson.it online or a participating Dyson demo store in Italy between April 22nd and June 2nd (both inclusive) for the Dyson Gen5detect™ Absolute and Dyson Detect Submarine™, and between May 14th and June 2nd (both inclusive) for the Dyson V15 Detect™ Absolute.\n\n Claims must be made via the trade in portal within 14 days of the purchase.\n\n All traded in products must be sent to us before end of day July 14th 2024',
  faq_topic_1_q4: 'Who are the participating retailers?',
  faq_topic_1_a4: `[https://www.dyson.it (https://www.dyson.it)], participating Dyson Demo stores and selected retailers. These Dyson Stores are participating in the promotion:

  - Milano - Gae Aulenti
  - Milano - City Life
  - Torino - Le Gru
  - Roma - Porta di Roma
  - Venezia - Nave De Vero
  - Marcianise - Campania
  - Firenze - I Gigli
  - Bergamo – OrioCenter
  - Palermo - Forum Palermo
  - Carugate – Carosello
  - Verona – Adigeo
  - Bologna - Shopville Gran Reno
  - Napoli - Coin Vomero
  - Roma - Coin Cola di Rienzo.

    Dyson Online Italy participates

    Unieuro stores are participating as well. More details on [Unieuro](https://www.alchemyglobalsolutions.com/dyson/unieuro)`,
  faq_topic_1_q5: 'What are the terms of the trade in event?',
  faq_topic_1_a5:
    'When you purchase a qualifying Dyson product and trade in an and old vacuum, you will receive a cash payment to your nominated bank account.\n\nYou can read the full terms and conditions in the footer of this webpage.',
  faq_topic_1_q6: 'Do I need to live in Italy to use the program?',
  faq_topic_1_a6:
    'No, but you need to be able to drop off the device you want to trade-in at one of the designated collection points in Italy and have a bank account in Italy or another EEA country to participate in the trade in event.',
  faq_topic_1_q7: 'What details do I need to complete the form?',
  faq_topic_1_a7:
    'You will need the name and serial number of your new Dyson product, your purchase receipt,  the brand of the vacuum cleaner you are trading in, and your address details including your IBAN to receive payment.',
  faq_topic_1_q8: 'What if I have lost the purchase receipt for my new Dyson?',
  faq_topic_1_a8:
    'You will need to request a new receipt from your retailer. Without the receipt we will not be able to process your trade-in application.',
  faq_topic_1_q9: 'Who manages the Dyson trade in event?',
  faq_topic_1_a9:
    'The trade in event is managed on behalf of Dyson by [Alchemy](https://www.alchemyglobalsolutions.com), a global leader in the circular economy and trade-in marketplace.',
  faq_topic_1_q10: 'What happens after I make my trade in application?',
  faq_topic_1_a10:
    'You will receive an email stating application has been received and informing you of your trade in reference number.',
  faq_topic_1_q11: 'How long will my trade in event application take to be approved?',
  faq_topic_1_a11:
    'Your application will be reviewed and a decision made on approval within 1 working day (Mon-Fri).\n\nUpon approval of your application you will be emailed with the approval and with a prepaid shipping label that must be printed and placed on the box of the old vacuum product to be sent to us.',
  faq_topic_1_q12: 'I didn’t receive an email or my application was not approved, who can I contact?',
  faq_topic_1_a12:
    'You can contact Alchemy’s customer service team at [dyson@alchemyglobalsolutions.com](mailto:dyson@alchemyglobalsolutions.com) or via the chat function on the trade in page on the Dyson website.',
  // -------------------------------------
  // Shipping Section
  // -------------------------------------
  faq_topic_2_title: 'Packaging and Shipping Your old Vacuum',
  faq_topic_2_q1: 'How do I package up my old vacuum cleaner?',
  faq_topic_2_a1:
    'You need to use a suitable box to send in your old vacuum product, the product should be packed inside securely and without excess space in the box. Please try and use a box that is of the right size.\n\nYou will attach the prepaid shipping label securely to that box.',
  faq_topic_2_q2: 'Can I use any type of box?',
  faq_topic_2_a2:
    'Yes, any suitable cardboard box. You can use the original box from the vacuum if you still have it, or any other suitable box.',
  faq_topic_2_q3: 'What do I need to send in the box',
  faq_topic_2_a3:
    '* The Trade In Vacuum must be intact and whole, it must not be broken into pieces.\n\n* It must be a regular full sized, corded or battery powered vacuum cleaner.\n\n* You must make sure that the dust collection bin or bag is empty.\n\n* You do not need to send the wands, hoses, cables, batteries or any other accessories. They are not required, only the main unit.\n\n* You must trade in only one eligible vacuum product.',
  faq_topic_2_q4: 'Shipment of batteries',
  faq_topic_2_a4:
    '* Please note - If the device has a battery and you wish to send it in, it must be intact and free from physical damage.\n\n* Do not send any damaged batteries in the package.\n\n* Do not use a hazardous good label – a single battery shipment does not require a hazardous goods label.',
  faq_topic_2_q5: 'How do I prepare the package for shipment',
  faq_topic_2_a5:
    '* Make sure that the box is sealed and secure\n\n* Print and securely attach the prepaid shipping label provided by us via email to the package\n\n* Drop off the package to a DHL drop off centerl or arrange a DHL courier collection from your address.\n\nPlease note:\n\nThe package weight is initially set at 5kg (as we don’t know it yet). DHL will determine the exact weight of your package (either at the beginning or possibly later) and charge us accordingly.\n\nDo not drop off your package at other non-DHL acceptance point, as it will not be accepted there.',
  // -------------------------------------
  // After Shipping Section
  // -------------------------------------
  faq_topic_3_title: 'After I have traded in my vacuum product',
  faq_topic_3_q1: 'How will I know when you have received my traded in vacuum ',
  faq_topic_3_a1:
    'We will email you when we have received your vacuum product and confirm that we have it and that the claim amount will be paid to the details you provided in your trade in application.',
  faq_topic_3_q2: 'How long will it take to confirm receipt?',
  faq_topic_3_a2:
    'We will typically confirm receipt of the traded in vacuum cleaner in our depot to you via email within 7 days of its shipment by you.',
  faq_topic_3_q3: 'What happens in the event that the trade in vacuum is disputed?',
  faq_topic_3_a3:
    'In the event that what you sent us does not match what you claimed on your trade in application our customer services team will get in touch with you immediately.',
  faq_topic_3_q4: 'How long will it take for me to get my trade in payment?',
  faq_topic_3_a4:
    'After verification of your new qualifying Dyson purchase, and receipt of your trade in vacuum, we will send the payment to your nominated account between 30 days and 45 days after we have received your product',
  // -------------------------------------
  // General Section
  // -------------------------------------
  faq_topic_4_title: 'General',
  faq_topic_4_q1: 'How do I get in touch with the trade in event customer service team',
  faq_topic_4_a1:
    'If you have any queries about the trade in event or your application you can reach the Alchemy team at [dyson@alchemyglobalsolutions.com](mailto:dyson@alchemyglobalsolutions.com).\n\nYou can also reach the Alchemy team via the chat function on the trade in page on the Dyson website.',
  faq_topic_4_q2: 'What are your hours of support',
  faq_topic_4_a2:
    'We are available weekdays from 9am – 5pm, we aim to respond to all queries within a maximum of one working day.',
  /** -------------------------------------------------------------------------- *
   * Quiz
   * -------------------------------------------------------------------------- */
  // -------------------------------------
  // Customer Section
  // -------------------------------------
  quiz_s1_title: 'Your details',
  quiz_s1_intro:
    'We will contact you to tell you when we have received your trade in vacuum product and processed your payment',
  quiz_s1_q1_label: 'What is your full name?',
  quiz_s1_q1_placeholder: 'Full name',
  quiz_s1_q1_error_required: 'Please enter your full name',
  quiz_s1_q1_error_invalid: 'Please enter both your first and last name',
  quiz_s1_q2_label: 'What is your email address?',
  quiz_s1_q2_placeholder: 'yourname@example.fr',
  quiz_s1_q2_error_required: 'Please enter your email address',
  quiz_s1_q2_error_invalid: 'Please enter a valid email address',
  quiz_s1_button: 'Continue',
  quiz_s1_button_hint: 'Please complete all fields before continuing',
  // -------------------------------------
  // Purchase Section
  // -------------------------------------
  quiz_s2_title: 'Verify your recent purchase',
  quiz_s2_short_title: 'Verify purchase',
  quiz_s2_intro:
    'We can only accept a trade in vacuum if you have purchased an eligible vacuum from Dyson in the time outlined for the promotion.',
  quiz_s2_next_step: 'Next: Vacuum to trade in',
  quiz_s2_button: 'Verify',
  quiz_s2_q1_label: 'Which model did you purchase?',
  // -------------------------------------
  quiz_s2_serial_title: 'Your serial number',
  quiz_s2_serial_help_button: 'Where can I find it?',
  quiz_s2_serial_q1_label: 'Upload image of serial number',
  quiz_s2_serial_q1_placeholder: 'Upload an image • Max 10MB',
  quiz_s2_serial_q2_label: 'Serial number',
  quiz_s2_serial_q2_placeholder: '000-00-00000000',
  quiz_s2_serial_q2_error_required: 'Please enter a serial number',
  quiz_s2_serial_q2_error_schema: 'Please enter a valid serial number',
  quiz_s2_serial_q2_error_maxlength: 'Maximum of 15 characters allowed',
  // -------------------------------------
  quiz_s2_receipt_title: 'Your receipt',
  quiz_s2_receipt_q1_label: 'Upload image of receipt',
  quiz_s2_receipt_q1_placeholder: 'Upload an image • Max 10MB',
  quiz_s2_receipt_q2_label: 'Date of purchase',
  quiz_s2_receipt_q2_placeholder: 'DD / MM / YYYY',
  quiz_s2_receipt_q2_error_required: 'Please enter a date',
  quiz_s2_receipt_q2_error_invalid: 'Must be a valid date',
  quiz_s2_receipt_q2_error_length: 'Must be a valid date',
  quiz_s2_receipt_q2_error_future: 'Date cannot be in the future',
  quiz_s2_receipt_q2_error_range: 'Date must be between 04/22/2024 and 06/02/2024',
  quiz_s2_receipt_q3_label: 'Retailer',
  quiz_s2_receipt_q3_placeholder: 'Please select',
  quiz_s2_receipt_q3_error_required: 'Please select a retailer',
  // -------------------------------------
  quiz_s2_file_error_required: 'You must upload a file',
  quiz_s2_file_error_size: 'File too large',
  quiz_s2_file_error_format: 'Unsupported File Format',
  quiz_s2_file_error_invalid: 'Invalid image',
  quiz_s2_file_error_processing: 'Error processing the image',
  quiz_s2_file_select: 'Select a file',
  // -------------------------------------
  serial_under_battery_title: 'On the underside of the battery pack',
  serial_under_battery_desc: 'The serial number is printed on the back of the battery pack at the base of the handle',
  serial_behind_bin_title: 'Behind the clear bin',
  serial_behind_bin_desc: 'Look on the underside of the clear bin opposite the trigger to reveal the serial number',
  serial_inside_handle_title: 'Inside the handle',
  serial_inside_handle_desc: 'Remove the click-in battery to reveal the serial number inside the machine’s handle',
  serial_helper_exit_button: 'Dismiss',
  // -------------------------------------
  // Trade-in Section
  // -------------------------------------
  quiz_s3_title: 'Your trade in vacuum product',
  quiz_s3_short_title: 'Trade in valuation',
  quiz_s3_intro: 'Tell us about the vacuum product you want to trade in and we will tell you its value',
  quiz_s3_next_step: 'Next: Contact address',
  quiz_s3_button: 'Check value',
  // -------------------------------------
  tradein_q1_label: 'Who is the manufacturer?',
  tradein_q1_option_1: 'Dyson',
  tradein_q1_option_2: 'Other',
  tradein_serial_label: 'What is the serial number? (optional)',
  tradein_serial_hint: '',
  tradein_q2_label: 'Confirmation of the Dyson Trade In Terms',
  tradein_q2_answer: 'I agree that my trade in meets the',
  tradein_q2_link: 'conditions of the program',
  tradein_q2_term_1: 'My vacuum is intact and whole.',
  tradein_q2_term_2: 'My vacuum is a regular full-sized corded or battery powered unit.',
  tradein_q2_term_3: "My vacuum's dust collection bin or bag is empty.",
  tradein_q2_term_4: 'I am only trading a single eligible vacuum cleaner.',
  // -------------------------------------
  valuation_title: 'Your vacuum is worth',
  valuation_desc_1:
    'We’ll pay you between 30 and 45 days after we have received your trade-in vacuum product at our facility.',
  valuation_desc_2:
    'If there are any discrepancies between what you describe and what we receive, the valuation could be affected.',
  valuation_button: 'Continue',
  // -------------------------------------
  // Shipping Section
  // -------------------------------------
  shipping_title: 'Contact address',
  shipping_short_title: 'Contact address',
  shipping_intro: 'We need to know your address so that we can create your free postage label',
  shipping_next_step: 'Next: Payment details',
  shipping_button: 'Continue',
  shipping_q1_label: 'Lookup your address',
  shipping_q1_placeholder: 'Start typing an address',
  shipping_address_label: 'Street address',
  shipping_address_placeholder: '',
  shipping_address_error_required: 'Please enter a street address',
  shipping_city_label: 'City',
  shipping_city_placeholder: '',
  shipping_city_error_required: 'Please enter a city',
  shipping_postcode_label: 'Post code',
  shipping_postcode_placeholder: '',
  shipping_postcode_error_required: 'Please enter a post code',
  shipping_postcode_error_invalid: 'Post code must be between 3 to 10 characters',
  shipping_country_label: 'Country',
  shipping_country_placeholder: 'Please select',
  shipping_country_error_required: 'Please select a country',
  shipping_matched_address: 'Your address',
  shipping_no_address_match: "Don't see your address? Add it manually.",
  shipping_back_button: 'Back',
  // -------------------------------------
  // Payment Section
  // -------------------------------------
  payment_title: 'Payment details',
  payment_short_title: 'Payment details',
  payment_intro:
    'Tell us the bank account details of who you would like us to make the payment to. Please check this information carefully as we cannot be liable for any mistakes.',
  payment_next_step: 'Final step',
  // -------------------------------------
  payment_account_label: 'Account holder',
  payment_account_placeholder: 'Full name of account holder',
  payment_account_error_required: 'Please enter the account holder name',
  payment_account_error_invalid: 'Please enter full name',
  // -------------------------------------
  payment_iban_label: 'IBAN',
  payment_iban_placeholder: 'e.g. FR00 0000 0000 0000 0000 0000 000',
  payment_iban_error_required: 'Please enter your IBAN',
  payment_iban_error_invalid: 'Please enter a valid IBAN',
  // -------------------------------------
  payment_submit_button: 'Submit',
  payment_submit_hint: 'Please complete all fields before submitting',
  // -------------------------------------
  payment_terms_text_1: 'I have read and accept the',
  payment_terms_link_1: 'Terms and Conditions',
  payment_terms_text_2: 'and',
  payment_terms_link_2: 'Privacy Policy',
  payment_terms_text_3: ' ',
  payment_terms_error_required: 'Please accept the terms and conditions',
  // -------------------------------------
  confirm_title: 'Confirm payment details',
  confirm_desc: 'Please make sure the payment details you have entered are correct.',
  confirm_account_label: 'Account Holder',
  confirm_iban_label: 'IBAN',
  confirm_button: 'Confirm',
  confirm_back_button: 'Back',

  // -------------------------------------
  // Order Confirmation Section
  // -------------------------------------
  order_title_1: 'Thank you',
  order_title_2: 'your Trade In has been created!',
  order_confirmation_title: 'What are the next steps?',
  order_confirmation_step1_title: 'Sit tight while we verify your details',
  order_confirmation_step1_content:
    'We need to do some final checks on the details you’ve given us - but don’t worry, this is expected and should take no longer than 1 working day.\n\nIf for some reason we need more information to complete our checks, we’ll contact you by email.',
  order_confirmation_step2_title: 'Pack your trade in vacuum and post it',
  order_confirmation_step2_content:
    'We will email your free shipping label to the email you provided.\n\nWhen you receive it, please pack your Trade In vacuum cleaner into a suitable box and attach your free pre-paid shipping label to it. Instructions for shipping will be included in the email.',
  order_confirmation_step3_title: 'Get paid!',
  order_confirmation_step3_contentA:
    'We will inspect your vacuum cleaner when we receive it and if it as described we will make a payment to the account provided 30 to 45 days after receiving your product:',
  order_confirmation_step3_contentB:
    'We will inspect your vacuum cleaner when we receive it and if it as described we will make a payment to the account provided (30 to 45 days after receiving your product):',
  order_confirmation_step3_contentC: 'Account holder',
  order_confirmation_step3_contentD: 'The payment will be made 30 to 45 days after we receive your product.',
  /** -------------------------------------------------------------------------- *
   * Errors
   * -------------------------------------------------------------------------- */
  server_error_Z6S:
    'We could not create the order as the serial number has already been registered. Please contact customer support.',
  /** -------------------------------------------------------------------------- *
   * Cookie Consent
   * -------------------------------------------------------------------------- */
  cookie_statement:
    'By clicking “Accept All Cookies”, you agree to the storing of cookies on your device to enhance site navigation, analyze site usage, and assist in our marketing efforts.',
  cookie_settings_button: 'Cookie settings',
  cookie_accept_all_button: 'Accept all cookies',
  cookie_allow_selected: 'Accept selected cookies',
  cookie_allow_all: 'Accept all cookies',
  cookie_essential_title: 'Essential',
  cookie_essential_desc:
    'These cookies are necessary for the website to function and cannot be deactivated in your systems. Typically, these cookies are only set in response to actions you take that correspond to a service request, such as setting your privacy preferences, logging in or filling out forms. You can set your browser to block these cookies or to notify you about these cookies. However, some areas of the website will not work. These cookies do not store any personal data.',
  cookie_performance_title: 'Performance',
  cookie_performance_desc:
    'These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our website. They help us answer questions about which pages are most popular, which are least used and how visitors move around the website. All information collected by these cookies is aggregated and therefore anonymous. If you do not allow these cookies, we will not be able to know when you have visited our website.',
  cookie_functional_title: 'Functional',
  cookie_functional_desc:
    'These cookies enable the website to provide enhanced functionality and personalization. They can be set by us or by third parties whose services we use on our sites. If you do not allow these cookies, some or all of these services may not function properly.',
  cookie_targetting_title: 'Targeting',
  cookie_targetting_desc:
    'These cookies may be set through our website by our advertising partners. They may be used by these companies to build a profile of your interests and show you relevant ads on other websites. They do not directly store personal data, but are based on unique identification of your browser and internet device. If you do not allow these cookies, you will experience less targeted advertising.',
  /** -------------------------------------------------------------------------- *
   * Policy Pages
   * -------------------------------------------------------------------------- */
  terms_body: terms,
  privacy_body: privacy,
  cookies_body: cookies,
}

export default en
